import type { infer as zodInfer, ZodType } from 'zod';
import { atomWithStorage } from 'jotai/utils';
import type { WritableAtom } from 'jotai';

const PERSISTED_ATOM_PREFIX = 'persisted_atom';

/* Get prefixed localStorage key */
function getPrefixedKey(
  key: string,
): `${typeof PERSISTED_ATOM_PREFIX}_${typeof key}` {
  return `${PERSISTED_ATOM_PREFIX}_${key}`;
}

export interface PersistedAtomOptions<T extends ZodType> {
  key: string;
  schema: T;
  defaultValue: zodInfer<T>;
  /**
   * Reads value from storage immediately on initialization with a minor
   * performance penalty, usually not needed
   */
  getOnInit?: boolean;
}

export type SchemaFromPersistedAtom<T> =
  T extends WritableAtom<infer U, unknown[], unknown> ? U : never;

export function persistedAtom<T extends ZodType>({
  key,
  schema,
  defaultValue,
  getOnInit,
}: PersistedAtomOptions<T>) {
  return atomWithStorage<zodInfer<T>>(
    key,
    defaultValue,
    {
      getItem(key, initialValue) {
        if (typeof globalThis === 'undefined' || !globalThis.localStorage)
          return initialValue;
        const storedValue = localStorage.getItem(getPrefixedKey(key));

        try {
          return schema.parse(JSON.parse(storedValue ?? ''));
        } catch (err) {
          if (storedValue !== null) {
            console.warn(`Failed to parse persisted atom ${key}:`, err);
          }
          return initialValue;
        }
      },
      setItem(key, value) {
        localStorage.setItem(getPrefixedKey(key), JSON.stringify(value));
      },
      removeItem(key) {
        localStorage.removeItem(getPrefixedKey(key));
      },
      subscribe(key, callback, initialValue) {
        if (
          typeof globalThis === 'undefined' ||
          typeof globalThis.addEventListener === 'undefined' ||
          !globalThis.Storage
        ) {
          console.warn(
            `Tried to subscribe to atom ${key} in non-browser environment`,
          );
          return () => void undefined;
        }

        const storageEventCallback = (e: StorageEvent) => {
          if (e.storageArea === localStorage && e.key === key) {
            let newValue;
            try {
              newValue = schema.parse(JSON.parse(e.newValue ?? ''));
            } catch {
              newValue = initialValue;
            }
            callback(newValue);
          }
        };

        globalThis.addEventListener('storage', storageEventCallback);

        return () => {
          globalThis.removeEventListener('storage', storageEventCallback);
        };
      },
    },
    { getOnInit },
  );
}
